import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronRight, CheckCircle, AlertTriangle, Camera, Save, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IQCForm = ({ user, onLogout, hideHeader = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState({
    imei: '',
    model: '',
    brand: '',
    os: 'Android'
  });
  const [responses, setResponses] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [generalNotes, setGeneralNotes] = useState('');
  const [iqcId, setIqcId] = useState(null);

  // Fetch IQC template when OS changes
  useEffect(() => {
    if (deviceInfo.os) {
      fetchTemplate(deviceInfo.os);
    }
  }, [deviceInfo.os]);

  const fetchTemplate = async (os) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/iqc/templates/${os}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplate(response.data);
      
      // Expand first category by default
      if (response.data.categories && response.data.categories.length > 0) {
        setExpandedCategories({ [response.data.categories[0].category_id]: true });
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      alert('Failed to load IQC template');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleResponseChange = (parameterId, value, option = null) => {
    setResponses(prev => {
      const existing = prev[parameterId] || {
        parameter_id: parameterId,
        selected_values: [],
        triggered_parts: [],
        severity: 'NTF'
      };

      let newResponse = { ...existing };

      if (option) {
        // For radio/dropdown/checkbox
        if (option.input_type === 'checkbox') {
          // Toggle checkbox
          const index = newResponse.selected_values.indexOf(value);
          if (index > -1) {
            newResponse.selected_values = newResponse.selected_values.filter(v => v !== value);
            // Remove triggered parts from this option
            const optionData = option.options.find(opt => opt.value === value);
            if (optionData) {
              newResponse.triggered_parts = newResponse.triggered_parts.filter(
                part => !optionData.triggers_parts.includes(part)
              );
            }
          } else {
            newResponse.selected_values = [...newResponse.selected_values, value];
            // Add triggered parts
            const optionData = option.options.find(opt => opt.value === value);
            if (optionData && optionData.triggers_parts) {
              newResponse.triggered_parts = [
                ...new Set([...newResponse.triggered_parts, ...optionData.triggers_parts])
              ];
              newResponse.severity = optionData.severity || 'NTF';
            }
          }
        } else {
          // Radio or dropdown - single selection
          newResponse.selected_values = [value];
          const optionData = option.options.find(opt => opt.value === value);
          if (optionData) {
            newResponse.triggered_parts = optionData.triggers_parts || [];
            newResponse.severity = optionData.severity || 'NTF';
          }
        }
      } else {
        // For text or number inputs
        if (typeof value === 'number') {
          newResponse.numeric_value = value;
        } else {
          newResponse.text_value = value;
        }
      }

      return { ...prev, [parameterId]: newResponse };
    });
  };

  const renderParameter = (parameter, category) => {
    const response = responses[parameter.parameter_id] || {};
    const selectedValues = response.selected_values || [];

    const getSeverityColor = (severity) => {
      switch (severity?.toLowerCase()) {
        case 'ntf': return 'text-green-600';
        case 'low': return 'text-yellow-500';
        case 'medium': return 'text-orange-500';
        case 'high': return 'text-red-500';
        case 'critical_l1':
        case 'critical_l4': return 'text-red-700';
        default: return 'text-gray-600';
      }
    };

    return (
      <div key={parameter.parameter_id} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">
              {parameter.name}
              {parameter.is_required && <span className="text-red-500 ml-1">*</span>}
            </h4>
            {parameter.help_text && (
              <p className="text-sm text-gray-500 mt-1">{parameter.help_text}</p>
            )}
          </div>
          {response.triggered_parts && response.triggered_parts.length > 0 && (
            <div className="ml-4">
              <AlertTriangle className={`w-5 h-5 ${getSeverityColor(response.severity)}`} />
            </div>
          )}
        </div>

        {/* Render input based on type */}
        {parameter.input_type === 'radio' && (
          <div className="space-y-2 mt-3">
            {parameter.options.map(option => (
              <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name={parameter.parameter_id}
                  value={option.value}
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleResponseChange(parameter.parameter_id, option.value, parameter)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">{option.value}</span>
                {option.triggers_parts && option.triggers_parts.length > 0 && (
                  <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(option.severity)} bg-white`}>
                    {option.triggers_parts.join(', ')}
                  </span>
                )}
              </label>
            ))}
          </div>
        )}

        {parameter.input_type === 'dropdown' && (
          <select
            value={selectedValues[0] || ''}
            onChange={(e) => handleResponseChange(parameter.parameter_id, e.target.value, parameter)}
            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select an option...</option>
            {parameter.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.value}
                {option.triggers_parts && option.triggers_parts.length > 0 && ` (${option.triggers_parts.join(', ')})`}
              </option>
            ))}
          </select>
        )}

        {parameter.input_type === 'checkbox' && (
          <div className="space-y-2 mt-3">
            {parameter.options.map(option => (
              <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  value={option.value}
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleResponseChange(parameter.parameter_id, option.value, parameter)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">{option.value}</span>
                {option.triggers_parts && option.triggers_parts.length > 0 && (
                  <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(option.severity)} bg-white`}>
                    {option.triggers_parts.join(', ')}
                  </span>
                )}
              </label>
            ))}
          </div>
        )}

        {parameter.input_type === 'number' && (
          <input
            type="number"
            min={parameter.min_value}
            max={parameter.max_value}
            placeholder={parameter.placeholder}
            value={response.numeric_value || ''}
            onChange={(e) => handleResponseChange(parameter.parameter_id, parseFloat(e.target.value))}
            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}

        {parameter.input_type === 'text' && (
          <input
            type="text"
            placeholder={parameter.placeholder}
            value={response.text_value || ''}
            onChange={(e) => handleResponseChange(parameter.parameter_id, e.target.value)}
            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}

        {/* Show triggered parts summary */}
        {response.triggered_parts && response.triggered_parts.length > 0 && (
          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm font-medium text-yellow-800">
              ⚠️ Parts Required: {response.triggered_parts.join(', ')}
            </p>
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (isDraft = false) => {
    // Validate device info
    if (!deviceInfo.imei || !deviceInfo.model || !deviceInfo.brand) {
      alert('Please fill in all device information');
      return;
    }

    // If not draft, validate required fields
    if (!isDraft && template) {
      for (const category of template.categories) {
        for (const param of category.parameters) {
          if (param.is_required && !responses[param.parameter_id]) {
            alert(`Please complete required field: ${param.name}`);
            return;
          }
        }
      }
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // First, create IQC record if not exists
      let currentIqcId = iqcId;
      if (!currentIqcId) {
        const createResponse = await axios.post(
          `${API_URL}/api/iqc/start`,
          {
            device_imei: deviceInfo.imei,
            device_model: deviceInfo.model,
            device_brand: deviceInfo.brand,
            device_os: deviceInfo.os,
            engineer_id: user.id || user.employee_id,
            engineer_name: user.name
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        currentIqcId = createResponse.data.iqc_id;
        setIqcId(currentIqcId);
      }

      // Convert responses to array format
      const responsesArray = Object.values(responses).map(resp => ({
        ...resp,
        parameter_name: template.categories
          .flatMap(cat => cat.parameters)
          .find(p => p.parameter_id === resp.parameter_id)?.name || ''
      }));

      // Submit IQC
      const submitResponse = await axios.post(
        `${API_URL}/api/iqc/submit`,
        {
          iqc_id: currentIqcId,
          responses: responsesArray,
          general_notes: generalNotes,
          auto_create_requisition: !isDraft
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(isDraft ? 'IQC saved as draft' : 'IQC submitted successfully!');
      
      if (!isDraft && submitResponse.data.requisition_id) {
        alert(`Requisition ${submitResponse.data.requisition_id} auto-created`);
      }

      navigate('/trc');
    } catch (error) {
      console.error('Error submitting IQC:', error);
      alert(error.response?.data?.detail || 'Failed to submit IQC');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${hideHeader ? '' : 'py-6'}`}>
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        {!hideHeader && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Initial Quality Check (IQC)</h1>
            <p className="text-gray-600">Complete device quality assessment and identify required parts</p>
          </div>
        )}

        {/* Device Information */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Device Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IMEI <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={deviceInfo.imei}
                onChange={(e) => setDeviceInfo({ ...deviceInfo, imei: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter device IMEI"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Operating System <span className="text-red-500">*</span>
              </label>
              <select
                value={deviceInfo.os}
                onChange={(e) => setDeviceInfo({ ...deviceInfo, os: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Android">Android</option>
                <option value="iOS">iOS</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={deviceInfo.brand}
                onChange={(e) => setDeviceInfo({ ...deviceInfo, brand: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Samsung, Apple"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={deviceInfo.model}
                onChange={(e) => setDeviceInfo({ ...deviceInfo, model: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Galaxy S21, iPhone 13"
              />
            </div>
          </div>
        </div>

        {/* IQC Checklist */}
        {loading && <div className="text-center py-8">Loading template...</div>}
        
        {template && (
          <div className="space-y-4 mb-6">
            {template.categories.map(category => (
              <div key={category.category_id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleCategory(category.category_id)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {category.icon && <span className="text-2xl">{category.icon}</span>}
                    <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                    <span className="text-sm text-gray-500">({category.parameters.length} checks)</span>
                  </div>
                  {expandedCategories[category.category_id] ? (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  )}
                </button>

                {expandedCategories[category.category_id] && (
                  <div className="p-6">
                    {category.parameters.map(parameter => renderParameter(parameter, category))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* General Notes */}
        {template && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              General Notes (Optional)
            </label>
            <textarea
              value={generalNotes}
              onChange={(e) => setGeneralNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        )}

        {/* Action Buttons */}
        {template && (
          <div className="flex items-center justify-between bg-white rounded-lg shadow-sm p-6">
            <button
              onClick={() => navigate('/trc')}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <div className="flex space-x-3">
              <button
                onClick={() => handleSubmit(true)}
                disabled={loading}
                className="px-6 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>Save Draft</span>
              </button>
              <button
                onClick={() => handleSubmit(false)}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                <span>Submit IQC</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IQCForm;
